import React, { useEffect, useState } from 'react';
import {
  Cancel,
  ErrorRounded,
  Replay,
  WarningRounded
} from '@mui/icons-material';
import IntervalTimer from 'react-interval-timer';

import { useTheme } from 'contexts/ThemeContext';

import { isValidUrl } from 'utils/functions';
import Icon from 'utils/icons';
import moment from 'utils/moment';
import { ACTIVATION, PML } from 'utils/rest';

import { TableWithColapse, Text } from 'components/DataDisplay';
import { LinearProgress } from 'components/Feedback';
import { Button, Checkbox, Select, TextField } from 'components/Inputs';
import { Container, Item, Row } from 'components/Layout';
import { Loading } from 'components/Loading';
import { Accordion } from 'components/Surfaces';

export const SetupContainer = () => {
  const { themeColors } = useTheme();
  const [original, setOriginal] = useState(null);
  const [googleSheetUrl, setGoogleSheetUrl] = useState('');
  const [dailyImport, setDailyImport] = useState(false);
  const [googleSheetUrlError, setGoogleSheetUrlError] = useState(false);
  const [shopGroupOptions, setShopGroupOptions] = useState([]);
  const [activationShopGroups, setActivationShopGroups] = useState([]);
  const [config, setConfig] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [computing, setComputing] = useState(false);
  const [setupTask, setSetupTask] = useState({});
  const [configList, setConfigList] = useState([]);
  const [selectedShopGroup, setSelectedShopGroup] = useState('');
  const [warningsExpanded, setWarningExpanded] = useState(false);
  const [rows, setRows] = useState([]);
  const [progress, setProgress] = useState(0);
  const [shopIsInProgress, setShopIsInProgress] = useState(false);

  const isInprogress = setupTask?.status === 'inProgress';
  const isAborted = setupTask?.status === 'aborted';

  const onExpandChange = () => {
    setWarningExpanded(!warningsExpanded);
  };

  const getShopStatusTxt = status => {
    switch (status) {
      case 'toDo':
        return (
          <Text component="span" fontWeight={400} color="pml">
            Programmé
          </Text>
        );
      case 'inProgress':
        return (
          <Text component="span" fontWeight={400} color="pml">
            En cours
          </Text>
        );
      case 'error':
        return (
          <Text component="span" fontWeight={400} color="error">
            Erreur
          </Text>
        );
      case 'done':
        return (
          <Text component="span" fontWeight={400} color="activation">
            Terminé
          </Text>
        );
      default:
        return (
          <Text component="span" fontWeight={400} color="activation">
            {status}
          </Text>
        );
    }
  };

  useEffect(() => {
    setShopGroupOptions(
      activationShopGroups.map(el => ({
        value: el._id,
        label: el.name
      }))
    );
  }, [activationShopGroups]);

  useEffect(() => {
    setGoogleSheetUrlError(!isValidUrl(googleSheetUrl));
  }, [googleSheetUrl]);

  const getLastTask = async currentConfig => {
    if (!currentConfig?.id) {
      return;
    }
    const task = await PML.getLastSetupTask(currentConfig.id);
    const shopKeys = Object.keys(setupTask?.shopStatus || {});
    const shopInProgress =
      shopKeys.some(
        key =>
          setupTask?.shopStatus[key]?.status === 'inProgress' ||
          setupTask?.shopStatus[key]?.status === 'toDo'
      ) || false;
    setComputing(task?.step === 'inProgress' || shopInProgress);
    const shopDone = shopKeys.filter(
      key =>
        setupTask?.shopStatus[key]?.status !== 'inProgress' &&
        setupTask?.shopStatus[key]?.status !== 'toDo'
    );
    const finalProgress = (shopDone.length || 0) / (shopKeys.length || 0);
    setProgress(finalProgress * 100);
    const parsedRows = shopKeys.map((el, index) => {
      const oldRow = rows.find(row => row.key === el);
      return {
        key: el,
        index,
        status: getShopStatusTxt(task.shopStatus?.[el]?.status),
        collapse: oldRow?.collapse || false,
        subRows: task?.shopStatus?.[el]?.message
          ? [{ message: task?.shopStatus?.[el]?.message }]
          : []
      };
    });
    setRows(parsedRows);
    setShopIsInProgress(shopInProgress);
    setSetupTask(task);
  };

  const getDatas = async () => {
    const tmpConfigList = await PML.getConfigs('custom');
    const tmpActivationShopGroups = await ACTIVATION.getActivationShopGoups();
    setConfigList(tmpConfigList);
    setActivationShopGroups(tmpActivationShopGroups);
    setSelectedShopGroup(tmpActivationShopGroups[0]?._id);
    setLoaded(true);
  };

  const getStatus = async () => {
    if (config) {
      getLastTask(config);
    }
  };

  const onSave = async () => {
    let newConfig;
    if (config._id) {
      newConfig = await PML.updateConfig(config._id, config);
    } else {
      newConfig = await PML.createConfig({ ...config, type: 'custom' });
    }

    setOriginal(newConfig);
    setConfig(newConfig);
    setConfigList(prev => {
      const index = prev.findIndex(el => el._id === newConfig._id);
      if (index !== -1) {
        // eslint-disable-next-line no-param-reassign
        prev[index] = newConfig;
      } else {
        prev.push(newConfig);
      }

      return prev;
    });
  };

  const onCompute = async () => {
    setComputing(true);
    await PML.computeCustom(config.id);
    await getStatus();
  };

  useEffect(() => {
    if (!loaded) {
      getDatas();
    }
    // eslint-disable-next-line
  }, [loaded]);

  const redirectToTemplate = () => {
    window.open(
      'https://docs.google.com/spreadsheets/d/1IEkaI-gtqy_hD2NJGz1_CV1XAtBESGHER1ZzEPtJkiM/edit?usp=sharing',
      '_blank',
      'noopener,noreferrer'
    );
  };

  useEffect(() => {
    if (selectedShopGroup) {
      const tmpConfig = configList.find(
        el => el.shopGroupId === selectedShopGroup
      );
      setConfig(tmpConfig || { shopGroupId: selectedShopGroup });
      setOriginal(tmpConfig || { shopGroupId: selectedShopGroup });
      setGoogleSheetUrl(tmpConfig?.googleSheetUrl || '');
      setDailyImport(tmpConfig?.daily ?? false);
      setSetupTask({});
    }
    // eslint-disable-next-line
  }, [selectedShopGroup]);

  const getStatusTxt = statusTask => {
    const shopKeys = Object.keys(statusTask.shopStatus || {});
    const inProgress = shopKeys.some(
      key =>
        setupTask?.shopStatus[key]?.status === 'inProgress' ||
        setupTask?.shopStatus[key]?.status === 'toDo'
    ); // on le fait aussi car quand on passe en vue par shops ça affiche terminé puis en cours
    switch (statusTask.status) {
      case 'inProgress':
        return (
          <Text component="span" fontWeight={400} color="pml">
            En cours
          </Text>
        );
      case 'error':
        return (
          <Text component="span" fontWeight={400} color="error">
            Erreur
          </Text>
        );
      case 'done':
        return (
          <Text
            component="span"
            fontWeight={400}
            color={inProgress ? 'pml' : 'activation'}
          >
            {inProgress ? 'En cours' : 'Terminé'}
          </Text>
        );
      default:
        return (
          <Text component="span" fontWeight={400} color="activation">
            {statusTask.status}
          </Text>
        );
    }
  };

  const onStop = () => {
    PML.stopSetupTask(setupTask.id);
  };

  if (!loaded) {
    return (
      <Container>
        <Loading loading />
      </Container>
    );
  }

  return (
    <Container>
      <IntervalTimer
        timeout={3000}
        callback={getStatus}
        enabled={!!config?._id}
        repeat
      />
      {shopGroupOptions.length !== 0 && (
        <Row>
          <Item>
            <Text fontWeight={700} fontSize="14px">
              Configuration plateforme multi-locale
            </Text>
          </Item>
          <Item>
            <Row alignItems="flex-start" spacing={4}>
              <Item xs={8} justify="flex-start">
                <Button
                  onClick={() => redirectToTemplate()}
                  uppercase
                  style={{
                    textDecoration: 'underline'
                  }}
                >
                  Ouvrir le template
                </Button>
              </Item>
            </Row>
            <Row alignItems="flex-start" spacing={4}>
              <Item xs={4} style={{ paddingTop: '0px' }}>
                <Select
                  title="Réseau"
                  value={selectedShopGroup}
                  onChange={setSelectedShopGroup}
                  options={shopGroupOptions}
                  small
                />
              </Item>
              <Item xs={4} style={{ paddingTop: '0px' }}>
                <TextField
                  title="URL du Google Sheet"
                  updateValue
                  small
                  value={googleSheetUrl}
                  onChange={url => {
                    setGoogleSheetUrl(url);
                    setConfig({ ...config, googleSheetUrl: url });
                  }}
                  error={googleSheetUrlError}
                />
              </Item>
            </Row>
            {/* <Row>
              <Item xs={4} justify="flex-start" style={{ paddingTop: '0px' }}>
                <Button
                  onClick={onAdd}
                  uppercase
                  style={{
                    textDecoration: 'underline'
                  }}
                >
                  Créer un nouveau réseau
                </Button>
              </Item>
              <Item xs={4} />
            </Row> */}
            <Row>
              <Item xs={4} justify="flex-start" style={{ paddingTop: '0px' }}>
                <Checkbox
                  checked={dailyImport}
                  disabled={!selectedShopGroup}
                  onChange={event => {
                    setDailyImport(event.target.checked);
                    setConfig({ ...config, daily: event.target.checked });
                  }}
                />
                <Text>Import quotidien</Text>
              </Item>
              <Item xs={4} />
            </Row>
          </Item>
          <Item>
            {(!setupTask || !isInprogress || !shopIsInProgress) && (
              <Row>
                <Item flex style={{ padding: '10px' }}>
                  <Button
                    onClick={onSave}
                    variant="contained"
                    disabled={
                      (original.googleSheetUrl === config.googleSheetUrl &&
                        !!original.daily === !!config.daily) ||
                      !config.googleSheetUrl ||
                      googleSheetUrlError
                    }
                    style={{
                      padding: '10px 16px',
                      backgroundColor:
                        (original.googleSheetUrl === config.googleSheetUrl &&
                          !!original.daily === !!config.daily) ||
                        !config.googleSheetUrl ||
                        googleSheetUrlError
                          ? themeColors.themeV2.white
                          : themeColors.themeV2.black
                    }}
                  >
                    <Icon icon="save" />
                    <Text
                      fontSize="inherit"
                      fontWeight="inherit"
                      color="inherit"
                    >
                      &nbsp; Sauvegarder
                    </Text>
                  </Button>
                </Item>
                {!!config.id && (
                  <Item flex style={{ padding: '10px' }}>
                    <Button
                      variant="contained"
                      onClick={() => onCompute()}
                      loading={!isAborted && computing}
                      color={
                        original.googleSheetUrl !== config.googleSheetUrl ||
                        googleSheetUrlError
                          ? themeColors.black
                          : undefined
                      }
                      disabled={
                        !isAborted &&
                        (isInprogress ||
                          shopIsInProgress ||
                          original.googleSheetUrl !== config.googleSheetUrl ||
                          !config.googleSheetUrl ||
                          googleSheetUrlError)
                      }
                      style={{ padding: '10px 16px' }}
                    >
                      <Container spacing={0}>
                        <Row spacing={0} alignItems="center">
                          <Item flex style={{ paddingRight: '10px' }}>
                            {computing ? (
                              <Loading
                                loading
                                width="24px"
                                color={themeColors.keep_light}
                              />
                            ) : (
                              <Icon
                                icon="flightTakeOff"
                                attrs={{ height: '24px' }}
                              />
                            )}
                          </Item>
                          <Item xs>
                            <Text
                              fontSize="inherit"
                              fontWeight="inherit"
                              color="inherit"
                            >
                              Importer la configuration
                            </Text>
                          </Item>
                        </Row>
                      </Container>
                    </Button>
                  </Item>
                )}
                {setupTask?.status === 'error' && setupTask.lastLine !== 0 && (
                  <Item flex style={{ padding: '10px' }}>
                    <Button
                      variant="contained"
                      onClick={() => onCompute(setupTask.lastLine)}
                      loading={computing}
                      color={
                        original.googleSheetUrl !== config.googleSheetUrl
                          ? themeColors.black
                          : undefined
                      }
                      disabled={
                        original.googleSheetUrl !== config.googleSheetUrl ||
                        !config.googleSheetUrl
                      }
                      style={{ padding: '10px 16px' }}
                    >
                      <Container spacing={0}>
                        <Row spacing={0} alignItems="center">
                          <Item flex style={{ paddingRight: '10px' }}>
                            {computing ? (
                              <Loading
                                loading
                                width="24px"
                                color={themeColors.keep_light}
                              />
                            ) : (
                              <Replay />
                            )}
                          </Item>
                          <Item xs>
                            <Text
                              fontSize="inherit"
                              fontWeight="inherit"
                              color="inherit"
                            >
                              Reprendre
                            </Text>
                          </Item>
                        </Row>
                      </Container>
                    </Button>
                  </Item>
                )}
              </Row>
            )}
            {(isInprogress || shopIsInProgress) && (
              <Row>
                <Item flex style={{ padding: '10px' }}>
                  <Button
                    onClick={onStop}
                    variant="contained"
                    color={themeColors.black}
                    disabled={!isInprogress && !shopIsInProgress}
                    style={{
                      padding: '10px 16px'
                    }}
                  >
                    <Cancel />
                    &nbsp; Arreter
                  </Button>
                </Item>
              </Row>
            )}
            {setupTask && (
              <Row justify="center">
                <Item>
                  <Text fontWeight={700}>
                    {isInprogress || shopIsInProgress
                      ? `Le fichier est en cours d'import`
                      : 'Historique de votre configuration :'}
                  </Text>
                </Item>
                {shopIsInProgress && (
                  <Item>
                    <LinearProgress
                      color={themeColors.pml}
                      value={progress}
                      variant="determinate"
                    />
                  </Item>
                )}
                <Item
                  flex
                  style={{
                    margin: '50px',
                    maxWidth: '450px'
                  }}
                >
                  <Row spacing={0}>
                    <Item justify="flex-start">
                      <Text fontWeight={700}>
                        Date d&apos;import :&nbsp;
                        <Text component="span" fontWeight={400}>
                          {moment(
                            isInprogress || shopIsInProgress
                              ? setupTask.createdAt
                              : setupTask.updatedAt
                          ).format('DD/MM/YYYY HH:mm:ss')}
                        </Text>
                      </Text>
                    </Item>
                    {(isInprogress || shopIsInProgress) && (
                      <Item justify="flex-start">
                        <Text fontWeight={700}>
                          En cours depuis&nbsp;:&nbsp;
                          <Text component="span" fontWeight={400}>
                            {`${moment(setupTask.updatedAt).diff(
                              moment(setupTask.createdAt),
                              'minutes'
                            )} minutes`}
                          </Text>
                        </Text>
                      </Item>
                    )}
                    <Item justify="flex-start">
                      <Text fontWeight={700}>
                        Status&nbsp;:&nbsp;
                        {getStatusTxt(setupTask)}
                      </Text>
                    </Item>
                    {setupTask.step && (
                      <Item justify="flex-start">
                        <Row spacing={0}>
                          <Item flex>
                            <Text fontWeight={700}>Dernière étape: &nbsp;</Text>
                          </Item>
                          <Item xs justify="flex-start">
                            <Text
                              component="span"
                              fontWeight={400}
                              style={{
                                whiteSpace: 'pre-wrap'
                              }}
                            >
                              {setupTask.step}
                            </Text>
                          </Item>
                        </Row>
                      </Item>
                    )}
                    {setupTask.message && (
                      <Item justify="flex-start">
                        <Row spacing={0}>
                          <Item justify="flex-start">
                            <Text fontWeight={700}>
                              Message d&lsquo;erreur &nbsp;
                            </Text>
                          </Item>
                          <Item
                            justify="flex-start"
                            style={{
                              backgroundColor: themeColors.primary_header,
                              padding: '10px',
                              overflowWrap: 'anywhere'
                            }}
                          >
                            <Text
                              color="keep_light"
                              fontWeight={400}
                              fontSize="10px"
                            >
                              {setupTask.message}
                            </Text>
                          </Item>
                        </Row>
                      </Item>
                    )}
                  </Row>
                </Item>
                {rows.length > 0 && (
                  <Row style={{ padding: '20px', marginBottom: '30px' }}>
                    <TableWithColapse
                      rows={rows}
                      subRowsBackgroundColor={themeColors.themeV2.white}
                      subRowBackgroundcolor={themeColors.themeV2.white}
                      viewedRows={10}
                      defaultSortKey="status"
                      header={[
                        {
                          key: 'key',
                          label: 'Key',
                          sortable: true
                        },
                        {
                          key: 'status',
                          label: 'Status',
                          sortable: true,
                          format: (element, row) => {
                            return (
                              <Item
                                style={
                                  row?.subRows?.length > 0
                                    ? { paddingLeft: '40px' }
                                    : {}
                                }
                              >
                                {element}
                              </Item>
                            );
                          }
                        }
                      ]}
                      subRowHeader={[
                        {
                          key: 'message',
                          label: 'Message',
                          sortable: true,
                          format: element => {
                            return (
                              <Item
                                style={{
                                  padding: '20px',
                                  margin: '5px',
                                  borderRadius: '5px',
                                  textAlign: 'left',
                                  alignItems: 'center',
                                  flexDirection: 'row',
                                  backgroundColor: themeColors.themeV2.errorBg
                                }}
                              >
                                <Item xs={1}>
                                  <ErrorRounded color="error" />
                                </Item>
                                <Item xs={11}>{element}</Item>
                              </Item>
                            );
                          }
                        }
                      ]}
                      handleCollapse={row => {
                        const updatedRows = rows.map(el => {
                          const newEl = el;
                          if (newEl.index === row.index) {
                            newEl.collapse = !el.collapse;
                          }
                          return newEl;
                        });
                        setRows(updatedRows);
                      }}
                    />
                  </Row>
                )}
                {setupTask && setupTask.warnings?.length > 0 && (
                  <Row>
                    <Accordion
                      onExpandChange={onExpandChange}
                      expanded={warningsExpanded}
                      iconColor={themeColors.pml}
                      rows={[
                        {
                          key: 'warnings',
                          label: (
                            <Row
                              alignItems="center"
                              justify="flex-start"
                              spacing={0}
                              style={{ padding: '0px 3px' }}
                            >
                              <Item xs justify="flex-start" alignItems="center">
                                <WarningRounded />
                                <Text
                                  noWrap
                                  fontWeight={700}
                                  style={{ paddingLeft: '7px' }}
                                >
                                  Avertissements ({setupTask.warnings.length})
                                </Text>
                              </Item>
                            </Row>
                          ),
                          items: setupTask.warnings.map(el => <Text>{el}</Text>)
                        }
                      ]}
                      underline
                    />
                  </Row>
                )}
              </Row>
            )}
          </Item>
        </Row>
      )}
    </Container>
  );
};

export default SetupContainer;
